import { Helmet } from '@dr.pogodin/react-helmet'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Container,
    FormControlLabel,
    Link,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import * as endPoints from '@/endpoints'
import { acceptErrorMessage } from '@/features/agreements/termsPolicy'
import useAuth from '@/hooks/useAuth'
import axios from '@/lib/axios'

/**
 * Shown in place of the dashboard to an organisation's own admin until their
 * organisation has accepted the published terms and data processing agreement
 * (launch plan decision Q3: every school accepts, even when a partner bills
 * it). One acceptance covers the organisation: its other admins are not asked
 * again for the same version.
 */
const AcceptTerms = () => {
    const { user, logout, refreshSession } = useAuth()
    const brand = useBranding()
    const productName = brand?.product_name || 'Power'
    const orgName = user?.org?.name || 'your organisation'
    const [role, setRole] = useState('')
    const [confirm, setConfirm] = useState(false)

    const terms = useQuery({
        queryKey: ['agreements', 'terms'],
        queryFn: () => axios.get(endPoints.AGREEMENTS_TERMS).then((r) => r.data)
    })

    const accept = useMutation({
        mutationFn: () =>
            axios
                .post(endPoints.AGREEMENTS_TERMS_ACCEPT, {
                    version: terms.data?.version ?? user?.terms?.version,
                    role: role.trim(),
                    confirm
                })
                .then((r) => r.data),
        onSuccess: () => refreshSession().catch(() => undefined)
    })

    const docLink = (href, label) => (
        <Link href={href} target="_blank" rel="noopener">
            {label}
        </Link>
    )

    return (
        <>
            <Helmet>
                <title>Accept the terms</title>
            </Helmet>
            <Box sx={{ display: 'flex', minHeight: '100%', px: 2, py: 8 }}>
                <Container maxWidth="sm">
                    <Card>
                        <CardContent>
                            <Stack spacing={2.5}>
                                <Typography variant="h4" component="h1">
                                    Before you start
                                </Typography>
                                <Typography color="text.secondary">
                                    {orgName} needs to accept the {productName}{' '}
                                    terms before anyone can use it. You only do
                                    this once for the whole organisation. Other
                                    admins won’t be asked.
                                </Typography>
                                <Typography component="div">
                                    Please read:
                                    <ul>
                                        <li>
                                            {docLink(
                                                '/terms',
                                                'Terms of service'
                                            )}
                                        </li>
                                        <li>
                                            {docLink(
                                                '/privacy',
                                                'Privacy policy'
                                            )}
                                        </li>
                                        {terms.data?.dpa_url && (
                                            <li>
                                                {docLink(
                                                    terms.data.dpa_url,
                                                    'Data processing agreement'
                                                )}
                                            </li>
                                        )}
                                    </ul>
                                </Typography>
                                <TextField
                                    label="Your job title"
                                    placeholder="For example, Bursar or Business manager"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    fullWidth
                                    required
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={confirm}
                                            onChange={(e) =>
                                                setConfirm(e.target.checked)
                                            }
                                        />
                                    }
                                    label={`I can agree to these for ${orgName}, and I accept them on its behalf.`}
                                />
                                {accept.isError && (
                                    <Alert severity="error">
                                        {acceptErrorMessage(accept.error)}
                                    </Alert>
                                )}
                                {terms.isError && (
                                    <Alert severity="error">
                                        We couldn’t load the terms. Reload the
                                        page to try again.
                                    </Alert>
                                )}
                                <Button
                                    variant="contained"
                                    size="large"
                                    disabled={
                                        !confirm ||
                                        !role.trim() ||
                                        terms.isLoading ||
                                        accept.isPending
                                    }
                                    onClick={() => accept.mutate()}
                                >
                                    Accept and continue
                                </Button>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Not the right person? Ask whoever signs
                                    contracts for {orgName} to sign in and
                                    accept.{' '}
                                    <Link
                                        component="button"
                                        variant="body2"
                                        onClick={() => logout()}
                                    >
                                        Log out
                                    </Link>
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        </>
    )
}

export default AcceptTerms
